import {
  DestroyRef,
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthActions } from '@core/store/auth/auth.actions';
import { Actions, ofActionDispatched, ofActionSuccessful } from '@ngxs/store';
import { AnalyticsService } from './analytics.service';
import { GtmLoader } from './gtm.loader';

/**
 * Starts Google Tag Manager and PostHog at app start and ties its identity to the Firebase session: identify on
 * `AuthActions.StateChanged` with a user (fresh login and reload with a persisted session) and on
 * `AuthActions.AccountLinked` (a sandbox visitor's account keeps their uid, so the sandbox and
 * sign-up events join under one person, D8), reset after a successful `AuthActions.SignOut`.
 * A sandbox visitor who enters an account they already have leaves the sandbox person behind
 * (line 469: the sandbox data is not migrated): reset before that account is identified, so only
 * a sign-in that succeeded drops the visitor's identity. Registered in AppModule.
 */
export function provideAnalytics(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const analytics = inject(AnalyticsService);
        const actions$ = inject(Actions);
        const destroyRef = inject(DestroyRef);

        inject(GtmLoader).load();
        analytics.init();

        /** The sandbox visitor PostHog identifies this browser as, while one is signed in. */
        let sandboxVisitorUid: string | null = null;

        actions$
          .pipe(
            ofActionDispatched(AuthActions.StateChanged, AuthActions.AccountLinked),
            takeUntilDestroyed(destroyRef)
          )
          .subscribe(({ user }) => {
            if (!user) return;
            if (sandboxVisitorUid !== null && sandboxVisitorUid !== user.uid) analytics.reset();
            sandboxVisitorUid = user.isAnonymous ? user.uid : null;
            analytics.identify(user.uid, user.email);
          });

        actions$
          .pipe(ofActionSuccessful(AuthActions.SignOut), takeUntilDestroyed(destroyRef))
          .subscribe(() => {
            sandboxVisitorUid = null;
            analytics.reset();
          });
      },
    },
  ]);
}
